import { useQuery } from "@tanstack/react-query";
import { callLcosService } from "../../data/lcosServices";
import { useAccessToken } from "../use-access-token";
import { useMemo } from "react";
import { useAccessControl } from "../../app/use-access-control";
import { FamilyContact, WithId } from "./types";
import { getCenterFamilies, getCenterFamilyContacts } from "./use-family-contract-endpoints";

export const useFindFamilyContact = (search?: string, enabled = true) => {
  const { getToken } = useAccessToken();
  const { activeCenterId } = useAccessControl();

  const query = useQuery({
    queryKey: ["findFamilyContact", activeCenterId, search],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: async () => findFamilyContact(await getToken(), activeCenterId!, search!),
    enabled: enabled && !!activeCenterId && !!search?.length
  });

  const contacts = useMemo(() => query.data?.contacts || [], [query.data]);

  return {
    contacts,
    // contacts: search ? contacts : [],
    isLoading: query.isLoading
  };
};

const findFamilyContact = (accessToken: string, centerId: string, search: string) => {
  return callLcosService<{ contacts: WithId<FamilyContact>[] }>(
    accessToken,
    `/api/centers/${centerId}/families/contacts?search=${encodeURIComponent(search)}`,
    "GET"
  );
};

export const useFindCenterFamilies = (search?: string) => {
  const { getToken } = useAccessToken();
  const { activeCenterId } = useAccessControl();

  const query = useQuery({
    queryKey: ["findCenterFamilies", activeCenterId, search],
    enabled: !!activeCenterId,
    queryFn: async () =>
      getCenterFamilies(await getToken(), activeCenterId, {
        search: search?.trim() || undefined
      })
  });

  const families = useMemo(() => query.data?.families ?? [], [query.data]);

  return {
    families,
    isLoading: query.isLoading
  };
};

export const useFindCenterFamilyContacts = (familyId?: string | null) => {
  const { getToken } = useAccessToken();
  const { activeCenterId } = useAccessControl();

  const query = useQuery({
    queryKey: ["findCenterFamilyContacts", activeCenterId, familyId],
    enabled: !!activeCenterId && !!familyId,
    queryFn: async () =>
      getCenterFamilyContacts(await getToken(), activeCenterId, {
        family_id: familyId ?? undefined
      })
  });

  const contacts = useMemo(
    () =>
      (query.data?.contacts ?? []).map(contact => ({
        id: contact.id,
        displayName: contact.displayName
      })),
    [query.data]
  );

  return {
    contacts,
    isLoading: query.isLoading
  };
};
